import { useEffect, useMemo, useState } from "react";
import {
  Wallet,
  Plus,
  X,
  Calculator,
  Building2,
  Receipt,
  TrendingUp,
  Trash2,
  CheckCircle2,
  RotateCcw,
  CircleDollarSign,
  Info,
  Eye,
  EyeOff,
  UploadCloud,
  Users,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useLanguage } from "../../context/LanguageContext";
import { tCrm, tFmt, crmApi, formatCurrency, logDevError } from "../../lib/crm";
import { toast } from "sonner";

const MODALITIES = ["tech", "general"];
const STATUS_COLORS = {
  pending: { bg: "bg-slate-500/15", text: "text-slate-300", border: "border-slate-500/30" },
  accrued: { bg: "bg-lime-500/15", text: "text-lime-300", border: "border-lime-500/30" },
  paid: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/30" },
  clawback: { bg: "bg-red-500/15", text: "text-red-300", border: "border-red-500/30" },
  expired: { bg: "bg-amber-500/15", text: "text-amber-300", border: "border-amber-500/30" },
  prospect: { bg: "bg-slate-500/15", text: "text-slate-300", border: "border-slate-500/30" },
  active: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/30" },
  gtx_managed: { bg: "bg-purple-500/15", text: "text-purple-300", border: "border-purple-500/30" },
};

export default function CRMCommissions({ me }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const isAdmin = me?.role === "admin";

  const [tab, setTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [operations, setOperations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [tiers, setTiers] = useState(null);
  const [showOpForm, setShowOpForm] = useState(false);
  const [showSupForm, setShowSupForm] = useState(false);
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [showOnlyDrafts, setShowOnlyDrafts] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [dash, ops, sups, pays, tiersRes] = await Promise.all([
        crmApi.get("/commissions/dashboard"),
        crmApi.get(`/commissions${showOnlyDrafts && isAdmin ? "?only_drafts=true" : ""}`),
        crmApi.get("/commissions/suppliers"),
        crmApi.get("/commissions/payouts"),
        crmApi.get("/commissions/tiers"),
      ]);
      setDashboard(dash.data);
      setOperations(ops.data || []);
      setSuppliers(sups.data || []);
      setPayouts(pays.data || []);
      setTiers(tiersRes.data);
      if (isAdmin) {
        const [ag, ldg] = await Promise.all([
          crmApi.get("/agents"),
          crmApi.get("/commissions/ledger"),
        ]);
        setAgents(ag.data || []);
        setLedger(ldg.data || []);
      }
    } catch (err) {
      logDevError("CRMCommissions.reload", err);
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnlyDrafts]);

  const tabs = [
    { id: "overview", label: t("comm.tabOverview"), icon: TrendingUp },
    { id: "operations", label: t("comm.tabOperations"), icon: Receipt },
    { id: "suppliers", label: t("comm.tabSuppliers"), icon: Building2 },
    ...(isAdmin ? [{ id: "ledger", label: t("comm.tabLedger"), icon: Users }] : []),
    ...(isAdmin ? [{ id: "payouts", label: t("comm.tabPayouts"), icon: CircleDollarSign }] : []),
    { id: "calculator", label: t("comm.tabCalculator"), icon: Calculator },
  ];

  const draftCount = useMemo(
    () => operations.filter((o) => !o.published).length,
    [operations]
  );

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="commissions-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Wallet className="w-7 h-7 text-lime-400" />
            {t("comm.title")}
          </h1>
          <p className="text-sm text-white/40 mt-1 max-w-2xl">{t("comm.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <Button
              onClick={() => setShowPayoutForm(true)}
              data-testid="generate-payout-btn"
              variant="outline"
              className="border-white/10 text-white/80"
            >
              <CircleDollarSign className="w-4 h-4 mr-1" />
              {t("comm.generatePayout")}
            </Button>
          )}
          <Button
            onClick={() => setShowSupForm(true)}
            data-testid="new-supplier-btn"
            variant="outline"
            className="border-white/10 text-white/80"
          >
            <Building2 className="w-4 h-4 mr-1" />
            {t("comm.newSupplier")}
          </Button>
          {isAdmin && (
            <Button
              onClick={() => setShowOpForm(true)}
              data-testid="new-operation-btn"
              className="bg-lime-400 hover:bg-lime-300 text-black font-semibold"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t("comm.newOperation")}
            </Button>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-white/8 overflow-x-auto">
        {tabs.map((tb) => {
          const Icon = tb.icon;
          return (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              data-testid={`comm-tab-${tb.id}`}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition border-b-2 flex items-center gap-2 whitespace-nowrap ${
                tab === tb.id ? "border-lime-400 text-white" : "border-transparent text-white/40 hover:text-white/80"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tb.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-white/40 text-sm">{t("common.loading")}</div>
      ) : (
        <>
          {tab === "overview" && <OverviewTab dashboard={dashboard} tiers={tiers} t={t} language={language} />}
          {tab === "operations" && (
            <OperationsTable
              operations={operations}
              isAdmin={isAdmin}
              t={t}
              onChange={reload}
              showOnlyDrafts={showOnlyDrafts}
              setShowOnlyDrafts={setShowOnlyDrafts}
              draftCount={draftCount}
            />
          )}
          {tab === "suppliers" && (
            <SuppliersTable suppliers={suppliers} isAdmin={isAdmin} t={t} onChange={reload} />
          )}
          {tab === "ledger" && isAdmin && <LedgerTable ledger={ledger} t={t} />}
          {tab === "payouts" && isAdmin && (
            <PayoutsTable payouts={payouts} agents={agents} t={t} onChange={reload} />
          )}
          {tab === "calculator" && <CalculatorTab tiers={tiers} t={t} />}
        </>
      )}

      {showOpForm && (
        <OperationForm
          suppliers={suppliers.filter((s) => isAdmin || s.agent_id === me.id)}
          onClose={() => setShowOpForm(false)}
          onCreated={() => {
            setShowOpForm(false);
            reload();
          }}
          t={t}
        />
      )}
      {showSupForm && (
        <SupplierForm
          isAdmin={isAdmin}
          agents={agents}
          onClose={() => setShowSupForm(false)}
          onCreated={() => {
            setShowSupForm(false);
            reload();
          }}
          t={t}
        />
      )}
      {showPayoutForm && isAdmin && (
        <PayoutForm
          agents={agents}
          onClose={() => setShowPayoutForm(false)}
          onCreated={() => {
            setShowPayoutForm(false);
            reload();
          }}
          t={t}
        />
      )}
    </div>
  );
}

// ============================================================
// Overview
// ============================================================
function OverviewTab({ dashboard, tiers, t, language }) {
  if (!dashboard || !tiers) return null;
  const totals = dashboard.totals || {};
  const cards = [
    { label: t("comm.kpiAccrued"), value: formatCurrency(totals.accrued), color: "text-lime-300" },
    { label: t("comm.kpiPaid"), value: formatCurrency(totals.paid), color: "text-emerald-300" },
    { label: t("comm.kpiPending"), value: formatCurrency(totals.pending), color: "text-slate-300" },
    { label: t("comm.kpiClawback"), value: formatCurrency(totals.clawback), color: "text-red-300" },
    { label: t("comm.kpiThisMonth"), value: formatCurrency(dashboard.this_month_accrued), color: "text-purple-300" },
    { label: t("comm.kpiSuppliers"), value: dashboard.supplier_count, color: "text-amber-300" },
  ];
  const pctFirst = Math.round((tiers.first_op_rate || 0) * 100);
  const pctResidual = Math.round((tiers.residual_rate || 0) * 100);

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="glass-strong rounded-2xl p-5 border border-white/5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mono">{c.label}</div>
            <div className={`mt-3 text-2xl font-bold ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Agreement rule cards */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass-strong rounded-2xl p-6 border border-white/8">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-lime-400" />
            <h3 className="text-sm font-semibold text-white/80">{t("comm.tierTech")}</h3>
          </div>
          <table className="w-full text-xs">
            <thead className="text-white/40 uppercase tracking-wider">
              <tr>
                <th className="text-left py-1">{t("comm.units")}</th>
                <th className="text-right py-1">{t("comm.feePerUnit")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(tiers.tech_tiers || []).map((tier) => (
                <tr key={`tech-${tier.min_units}`}>
                  <td className="py-1.5 mono">
                    {tier.min_units}{tier.max_units ? `–${tier.max_units}` : "+"}
                  </td>
                  <td className="py-1.5 text-right mono text-lime-300">€{tier.fee_per_unit.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="glass-strong rounded-2xl p-6 border border-white/8">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-lime-400" />
            <h3 className="text-sm font-semibold text-white/80">{t("comm.tierGeneral")}</h3>
          </div>
          <table className="w-full text-xs">
            <thead className="text-white/40 uppercase tracking-wider">
              <tr>
                <th className="text-left py-1">{t("comm.colValue")}</th>
                <th className="text-right py-1">{t("comm.feePercentage")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(tiers.general_tiers || []).map((tier) => (
                <tr key={`gen-${tier.max_value ?? "max"}`}>
                  <td className="py-1.5 mono">
                    {tier.max_value ? `≤ €${tier.max_value.toLocaleString()}` : "> €100,000"}
                  </td>
                  <td className="py-1.5 text-right mono text-lime-300">{(tier.percentage * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="glass-strong rounded-2xl p-6 border border-white/8 space-y-2 text-sm">
        <RuleLine text={tFmt(language, "comm.ruleFirst", { pct: pctFirst })} />
        <RuleLine text={tFmt(language, "comm.ruleResidual", { pct: pctResidual })} />
        <RuleLine text={t("comm.ruleExpired")} />
        <RuleLine text={t("comm.ruleNonCollection")} />
        <RuleLine text={t("comm.attributionLocked")} />
        <RuleLine text={t("comm.payoutPeriod")} />
      </div>
    </div>
  );
}

function RuleLine({ text }) {
  return (
    <div className="flex items-start gap-2 text-white/70 text-sm">
      <span className="w-1.5 h-1.5 rounded-full bg-lime-400 mt-2 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

// ============================================================
// Operations Table
// ============================================================
function OperationsTable({ operations, isAdmin, t, onChange, showOnlyDrafts, setShowOnlyDrafts, draftCount }) {
  const handleCollected = async (id) => {
    const dt = new Date().toISOString().slice(0, 10);
    try {
      await crmApi.post(`/commissions/${id}/mark-collected`, { collected_date: dt });
      toast.success("✓");
      onChange();
    } catch {
      toast.error(t("common.error"));
    }
  };

  const handlePublish = async (id) => {
    try {
      await crmApi.post(`/commissions/${id}/publish`);
      toast.success(t("comm.published"));
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const handleUnpublish = async (id) => {
    try {
      await crmApi.post(`/commissions/${id}/unpublish`);
      toast.success(t("comm.draft"));
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const handleBulkPublish = async () => {
    const ids = operations.filter((o) => !o.published).map((o) => o.id);
    if (ids.length === 0) return;
    try {
      const r = await crmApi.post("/commissions/publish-bulk", { operation_ids: ids });
      toast.success(`${t("comm.published")}: ${r.data?.published || 0}`);
      onChange();
    } catch {
      toast.error(t("common.error"));
    }
  };

  const handleClawback = async (id) => {
    const reason = window.prompt(t("comm.reasonClawback"));
    if (reason === null) return;
    try {
      await crmApi.post(`/commissions/${id}/clawback`, { reason });
      toast.success("✓");
      onChange();
    } catch {
      toast.error(t("common.error"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("comm.confirmDeleteOp"))) return;
    try {
      await crmApi.delete(`/commissions/${id}`);
      toast.success("✓");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <div className="space-y-3">
      {/* Toolbar — admin only */}
      {isAdmin && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-1">
          <label className="flex items-center gap-2 text-xs text-white/60">
            <input
              type="checkbox"
              checked={!!showOnlyDrafts}
              onChange={(e) => setShowOnlyDrafts(e.target.checked)}
              data-testid="show-only-drafts"
              className="w-3.5 h-3.5"
            />
            {t("comm.draftFiltered")}
            {draftCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px] mono">
                {draftCount}
              </span>
            )}
          </label>
          {draftCount > 0 && (
            <Button
              onClick={handleBulkPublish}
              data-testid="bulk-publish-btn"
              variant="outline"
              size="sm"
              className="border-lime-400/40 text-lime-300 hover:bg-lime-400/10"
            >
              <UploadCloud className="w-3.5 h-3.5 mr-1" />
              {t("comm.publishBulk")} ({draftCount})
            </Button>
          )}
        </div>
      )}
      {isAdmin && (
        <p className="text-[11px] text-white/40 italic px-1">{t("comm.publishingHint")}</p>
      )}

      {operations.length === 0 ? (
        <div className="text-white/40 text-sm p-6 text-center">{t("comm.noOperations")}</div>
      ) : (
        <div className="overflow-x-auto glass-strong rounded-xl border border-white/8">
      <table className="w-full text-sm" data-testid="operations-table">
        <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-white/50">
          <tr>
            <th className="text-left px-4 py-3">{t("comm.colSupplier")}</th>
            <th className="text-left px-4 py-3">{t("comm.colCode")}</th>
            <th className="text-left px-4 py-3">{t("comm.colModality")}</th>
            <th className="text-right px-4 py-3">{t("comm.colUnits")}</th>
            <th className="text-right px-4 py-3">{t("comm.colValue")}</th>
            <th className="text-right px-4 py-3">{t("comm.colFee")}</th>
            <th className="text-right px-4 py-3">{t("comm.colRate")}</th>
            <th className="text-right px-4 py-3">{t("comm.colAmount")}</th>
            <th className="text-left px-4 py-3">{t("comm.colStatus")}</th>
            {isAdmin && <th className="text-left px-4 py-3">{t("comm.published")}</th>}
            <th className="text-left px-4 py-3">{t("comm.colCollected")}</th>
            {isAdmin && <th className="text-right px-4 py-3">{t("comm.colActions")}</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {operations.map((op) => (
            <tr key={op.id} className="hover:bg-white/[0.03]" data-testid={`op-row-${op.id}`}>
              <td className="px-4 py-3">
                <div className="font-medium">{op.supplier_name || "—"}</div>
                <div className="text-[10px] text-white/40">{op.supplier_country}</div>
                {op.is_first_operation && (
                  <span className="inline-block text-[9px] mt-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    {t("comm.firstOpBadge")}
                  </span>
                )}
                {op.is_paired && (
                  <div
                    title={op.partner_agent_name ? `${t("comm.pairedPartner")}: ${op.partner_agent_name}` : ""}
                    data-testid={`op-paired-${op.id}`}
                    className="inline-flex items-center gap-1 text-[9px] mt-1 ml-1 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30"
                  >
                    <Users className="w-2.5 h-2.5" />
                    {op.share_role === "prospection" ? t("comm.pairedShareProspection") : t("comm.pairedShareClosing")}
                    {" · "}
                    {Math.round((op.split_pct ?? 0.5) * 100)}%
                  </div>
                )}
              </td>
              <td className="px-4 py-3 mono text-[11px] text-white/60">{op.gtx_operation_code || "—"}</td>
              <td className="px-4 py-3 text-xs">
                {op.modality === "tech" ? t("comm.modalityTech") : t("comm.modalityGeneral")}
              </td>
              <td className="px-4 py-3 text-right mono text-xs">{op.units || "—"}</td>
              <td className="px-4 py-3 text-right mono text-xs">
                {op.operation_value ? formatCurrency(op.operation_value) : "—"}
              </td>
              <td className="px-4 py-3 text-right mono text-xs">{formatCurrency(op.gtx_fee)}</td>
              <td className="px-4 py-3 text-right mono text-xs text-white/60">
                {Math.round((op.commission_rate || 0) * 100)}%
              </td>
              <td className="px-4 py-3 text-right mono font-semibold text-lime-300">
                {formatCurrency(op.commission_amount)}
              </td>
              <td className="px-4 py-3">
                <StatusPill status={op.status} t={t} />
              </td>
              {isAdmin && (
                <td className="px-4 py-3">
                  {op.published ? (
                    <span
                      data-testid={`pub-badge-${op.id}`}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                    >
                      {t("comm.publishedShort")}
                    </span>
                  ) : (
                    <span
                      data-testid={`draft-badge-${op.id}`}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border bg-amber-500/15 text-amber-300 border-amber-500/30"
                    >
                      {t("comm.draftShort")}
                    </span>
                  )}
                </td>
              )}
              <td className="px-4 py-3 text-[11px] text-white/50 mono">
                {op.collected_date ? op.collected_date.slice(0, 10) : "—"}
              </td>
              {isAdmin && (
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    {op.status === "pending" && (
                      <button
                        onClick={() => handleCollected(op.id)}
                        title={t("comm.markCollected")}
                        data-testid={`mark-collected-${op.id}`}
                        className="p-1.5 rounded hover:bg-lime-500/15 text-lime-300"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {!op.published ? (
                      <button
                        onClick={() => handlePublish(op.id)}
                        title={t("comm.publish")}
                        data-testid={`publish-${op.id}`}
                        className="p-1.5 rounded hover:bg-emerald-500/15 text-emerald-300"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      !op.payout_id && (
                        <button
                          onClick={() => handleUnpublish(op.id)}
                          title={t("comm.unpublish")}
                          data-testid={`unpublish-${op.id}`}
                          className="p-1.5 rounded hover:bg-amber-500/15 text-amber-300"
                        >
                          <EyeOff className="w-3.5 h-3.5" />
                        </button>
                      )
                    )}
                    {["accrued", "paid"].includes(op.status) && (
                      <button
                        onClick={() => handleClawback(op.id)}
                        title={t("comm.doClawback")}
                        data-testid={`clawback-${op.id}`}
                        className="p-1.5 rounded hover:bg-red-500/15 text-red-300"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {op.status !== "paid" && (
                      <button
                        onClick={() => handleDelete(op.id)}
                        title={t("comm.delete")}
                        data-testid={`delete-op-${op.id}`}
                        className="p-1.5 rounded hover:bg-red-500/15 text-red-300"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
      )}
    </div>
  );
}

function StatusPill({ status, t }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.pending;
  const labelKey = {
    pending: "statusPending",
    accrued: "statusAccrued",
    paid: "statusPaid",
    clawback: "statusClawback",
    expired: "statusExpired",
    prospect: "statusProspect",
    active: "statusActive",
    gtx_managed: "statusGtxManaged",
  }[status] || "statusPending";
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${colors.bg} ${colors.text} ${colors.border}`}>
      {t(`comm.${labelKey}`)}
    </span>
  );
}

// ============================================================
// Ledger Table (admin only — time & money per agent)
// ============================================================
function LedgerTable({ ledger, t }) {
  if (!ledger || ledger.length === 0) {
    return <div className="text-white/40 text-sm p-6 text-center">{t("comm.ledgerEmpty")}</div>;
  }
  return (
    <div className="space-y-3">
      <p className="text-xs text-white/40 italic px-1">{t("comm.ledgerSubtitle")}</p>
      <div className="overflow-x-auto glass-strong rounded-xl border border-white/8">
        <table className="w-full text-sm" data-testid="ledger-table">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-white/50">
            <tr>
              <th className="text-left px-4 py-3">{t("comm.colAgent")}</th>
              <th className="text-right px-4 py-3">{t("comm.colLedgerOps")}</th>
              <th className="text-right px-4 py-3">{t("comm.colDrafts")}</th>
              <th className="text-right px-4 py-3">{t("comm.kpiAccrued")}</th>
              <th className="text-right px-4 py-3">{t("comm.kpiPaid")}</th>
              <th className="text-right px-4 py-3">{t("comm.kpiClawback")}</th>
              <th className="text-right px-4 py-3">{t("comm.colMonthProgress")}</th>
              <th className="text-right px-4 py-3">{t("comm.colMonthlyGoal")}</th>
              <th className="text-left px-4 py-3">{t("comm.colFirstActivity")}</th>
              <th className="text-left px-4 py-3">{t("comm.colLastActivity")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {ledger.map((row) => {
              const goal = row.monthly_goal || 0;
              const progress = goal > 0 ? Math.min(100, Math.round((row.this_month_accrued * 100) / goal)) : 0;
              return (
                <tr key={row.agent_id} className="hover:bg-white/[0.03]" data-testid={`ledger-row-${row.agent_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{row.full_name || "—"}</div>
                    <div className="text-[10px] text-white/40">{row.email}</div>
                  </td>
                  <td className="px-4 py-3 text-right mono text-xs">{row.operations_count}</td>
                  <td className="px-4 py-3 text-right">
                    {row.draft_count > 0 ? (
                      <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 mono">
                        {row.draft_count}
                      </span>
                    ) : (
                      <span className="text-white/30 text-xs">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right mono font-semibold text-lime-300">
                    {formatCurrency(row.totals?.accrued)}
                  </td>
                  <td className="px-4 py-3 text-right mono font-semibold text-emerald-300">
                    {formatCurrency(row.totals?.paid)}
                  </td>
                  <td className="px-4 py-3 text-right mono text-red-300">
                    {row.totals?.clawback ? formatCurrency(row.totals.clawback) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="mono text-xs text-white">{formatCurrency(row.this_month_accrued)}</div>
                    {goal > 0 && (
                      <div className="mt-1 w-full h-1 bg-white/5 rounded overflow-hidden">
                        <div
                          className="h-1 bg-lime-400"
                          style={{ width: `${progress}%` }}
                          data-testid={`progress-${row.agent_id}`}
                        />
                      </div>
                    )}
                    {goal > 0 && <div className="text-[9px] text-white/40 mt-0.5">{progress}%</div>}
                  </td>
                  <td className="px-4 py-3 text-right mono text-xs text-white/60">
                    {goal ? formatCurrency(goal) : "—"}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-white/40 mono">
                    {row.first_activity ? row.first_activity.slice(0, 10) : "—"}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-white/40 mono">
                    {row.last_activity ? row.last_activity.slice(0, 10) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// Suppliers Table
// ============================================================
function SuppliersTable({ suppliers, isAdmin, t, onChange }) {
  if (suppliers.length === 0) {
    return <div className="text-white/40 text-sm p-6 text-center">{t("comm.noSuppliers")}</div>;
  }

  const handleDelete = async (id) => {
    if (!window.confirm("?")) return;
    try {
      await crmApi.delete(`/commissions/suppliers/${id}`);
      toast.success("✓");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <div className="overflow-x-auto glass-strong rounded-xl border border-white/8">
      <table className="w-full text-sm" data-testid="suppliers-table">
        <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-white/50">
          <tr>
            <th className="text-left px-4 py-3">{t("comm.colSupplier")}</th>
            <th className="text-left px-4 py-3">{t("comm.colCountry")}</th>
            <th className="text-left px-4 py-3">{t("comm.colModality")}</th>
            <th className="text-left px-4 py-3">{t("comm.colAgent")}</th>
            <th className="text-left px-4 py-3">{t("comm.colStatus")}</th>
            <th className="text-left px-4 py-3">{t("comm.colFirstClose")}</th>
            <th className="text-right px-4 py-3">{t("comm.colOps")}</th>
            {isAdmin && <th className="text-right px-4 py-3">{t("comm.colActions")}</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {suppliers.map((s) => (
            <tr key={s.id} className="hover:bg-white/[0.03]" data-testid={`sup-row-${s.id}`}>
              <td className="px-4 py-3">
                <div className="font-medium flex items-center gap-2">
                  {s.name}
                  {s.is_paired && (
                    <span
                      title={`${s.prospection_agent_name || "?"} → ${s.closing_agent_name || "?"}`}
                      data-testid={`sup-paired-badge-${s.id}`}
                      className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30"
                    >
                      <Users className="w-2.5 h-2.5" />
                      {Math.round((s.split_pct ?? 0.5) * 100)}/{100 - Math.round((s.split_pct ?? 0.5) * 100)}
                    </span>
                  )}
                </div>
                {s.contact_email && <div className="text-[10px] text-white/40">{s.contact_email}</div>}
                {s.is_paired && (
                  <div className="text-[10px] text-white/50 mt-0.5">
                    <span className="text-amber-300/80">{s.prospection_agent_name || "?"}</span>
                    {" → "}
                    <span className="text-emerald-300/80">{s.closing_agent_name || "?"}</span>
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-white/70">{s.country || "—"}</td>
              <td className="px-4 py-3 text-xs">
                {s.modality === "tech" ? t("comm.modalityTech") : t("comm.modalityGeneral")}
              </td>
              <td className="px-4 py-3 text-white/60 text-xs">{s.agent_name || "—"}</td>
              <td className="px-4 py-3">
                <StatusPill status={s.status || "prospect"} t={t} />
              </td>
              <td className="px-4 py-3 text-[11px] text-white/50 mono">
                {s.first_close_date ? s.first_close_date.slice(0, 10) : "—"}
              </td>
              <td className="px-4 py-3 text-right mono text-xs text-white/60">{s.operations_count || 0}</td>
              {isAdmin && (
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => handleDelete(s.id)}
                    data-testid={`delete-sup-${s.id}`}
                    className="p-1.5 rounded hover:bg-red-500/15 text-red-300"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Payouts Table (admin)
// ============================================================
function PayoutsTable({ payouts, agents, t, onChange }) {
  if (payouts.length === 0) {
    return <div className="text-white/40 text-sm p-6 text-center">{t("comm.noPayouts")}</div>;
  }
  const agentMap = Object.fromEntries(agents.map((a) => [a.id, a.full_name]));

  const markPaid = async (id) => {
    if (!window.confirm(t("comm.confirmMarkPaid"))) return;
    const invoice_ref = window.prompt("Invoice ref?") || "";
    try {
      await crmApi.post(`/commissions/payouts/${id}/mark-paid`, { invoice_ref });
      toast.success("✓");
      onChange();
    } catch {
      toast.error(t("common.error"));
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("?")) return;
    try {
      await crmApi.delete(`/commissions/payouts/${id}`);
      toast.success("✓");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <div className="overflow-x-auto glass-strong rounded-xl border border-white/8">
      <table className="w-full text-sm" data-testid="payouts-table">
        <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-white/50">
          <tr>
            <th className="text-left px-4 py-3">{t("comm.colAgent")}</th>
            <th className="text-left px-4 py-3">{t("comm.colPeriod")}</th>
            <th className="text-right px-4 py-3">{t("comm.colOps")}</th>
            <th className="text-right px-4 py-3">{t("comm.colTotal")}</th>
            <th className="text-left px-4 py-3">{t("comm.colStatus")}</th>
            <th className="text-left px-4 py-3">Invoice</th>
            <th className="text-right px-4 py-3">{t("comm.colActions")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {payouts.map((p) => (
            <tr key={p.id} className="hover:bg-white/[0.03]" data-testid={`payout-row-${p.id}`}>
              <td className="px-4 py-3 font-medium">{agentMap[p.agent_id] || p.agent_id}</td>
              <td className="px-4 py-3 mono text-[11px] text-white/60">
                {(p.period_start || "").slice(0, 10)} → {(p.period_end || "").slice(0, 10)}
              </td>
              <td className="px-4 py-3 text-right mono text-xs">{p.operation_count}</td>
              <td className="px-4 py-3 text-right mono font-semibold text-lime-300">
                {formatCurrency(p.total_amount, p.currency || "EUR")}
              </td>
              <td className="px-4 py-3">
                <StatusPill status={p.status === "pending" ? "accrued" : "paid"} t={t} />
              </td>
              <td className="px-4 py-3 text-[11px] mono text-white/60">{p.invoice_ref || "—"}</td>
              <td className="px-4 py-3 text-right">
                <div className="flex gap-1 justify-end">
                  {p.status === "pending" && (
                    <button
                      onClick={() => markPaid(p.id)}
                      data-testid={`mark-paid-${p.id}`}
                      className="p-1.5 rounded hover:bg-emerald-500/15 text-emerald-300"
                      title={t("comm.markPaid")}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {p.status !== "paid" && (
                    <button
                      onClick={() => cancel(p.id)}
                      data-testid={`cancel-payout-${p.id}`}
                      className="p-1.5 rounded hover:bg-red-500/15 text-red-300"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Calculator (live)
// ============================================================
function CalculatorTab({ tiers, t }) {
  const [form, setForm] = useState({
    modality: "tech",
    units: 100,
    operation_value: 0,
    is_first_operation: true,
    days_since_first_close: "",
  });
  const [result, setResult] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        const payload = {
          modality: form.modality,
          units: parseInt(form.units, 10) || 0,
          operation_value: parseFloat(form.operation_value) || 0,
          is_first_operation: !!form.is_first_operation,
          days_since_first_close: form.days_since_first_close === "" ? null : parseInt(form.days_since_first_close, 10),
        };
        const r = await crmApi.post("/commissions/calculate", payload);
        setResult(r.data);
      } catch (err) {
        logDevError("CRMCommissions.calc", err);
      }
    };
    run();
  }, [form]);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="glass-strong rounded-2xl p-6 border border-white/8 space-y-4" data-testid="calculator-form">
        <h3 className="text-sm font-semibold text-white/80">{t("comm.calcTitle")}</h3>
        <p className="text-xs text-white/40">{t("comm.calcDesc")}</p>

        <div>
          <Label className="text-white/70 text-xs">{t("comm.modality")}</Label>
          <select
            value={form.modality}
            onChange={(e) => setForm({ ...form, modality: e.target.value })}
            data-testid="calc-modality"
            className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
          >
            <option value="tech">{t("comm.modalityTech")}</option>
            <option value="general">{t("comm.modalityGeneral")}</option>
          </select>
        </div>

        {form.modality === "tech" ? (
          <Field
            label={t("comm.units")}
            type="number"
            value={form.units}
            onChange={(v) => setForm({ ...form, units: v })}
            testid="calc-units"
          />
        ) : (
          <Field
            label={t("comm.operationValue")}
            type="number"
            value={form.operation_value}
            onChange={(v) => setForm({ ...form, operation_value: v })}
            testid="calc-value"
          />
        )}

        <label className="flex items-center gap-2 text-sm text-white/70">
          <input
            type="checkbox"
            checked={form.is_first_operation}
            onChange={(e) => setForm({ ...form, is_first_operation: e.target.checked })}
            data-testid="calc-is-first"
            className="w-4 h-4"
          />
          {t("comm.isFirst")}
        </label>

        {!form.is_first_operation && (
          <Field
            label={t("comm.daysSinceFirst")}
            type="number"
            value={form.days_since_first_close}
            onChange={(v) => setForm({ ...form, days_since_first_close: v })}
            testid="calc-days"
          />
        )}
      </div>

      <div className="glass-strong rounded-2xl p-6 border border-lime-400/30 bg-lime-400/[0.03]" data-testid="calculator-result">
        <h3 className="text-sm font-semibold text-lime-300 mb-4 uppercase tracking-wider">Output</h3>
        {result ? (
          <div className="space-y-3">
            {result.fee_per_unit != null && (
              <Row label={t("comm.feePerUnit")} value={`€${(result.fee_per_unit || 0).toFixed(2)}`} />
            )}
            {result.fee_percentage != null && (
              <Row label={t("comm.feePercentage")} value={`${(result.fee_percentage * 100).toFixed(1)}%`} />
            )}
            <Row label={t("comm.gtxFee")} value={formatCurrency(result.gtx_fee)} highlight />
            <Row
              label={t("comm.commissionRate")}
              value={`${Math.round((result.commission_rate || 0) * 100)}%`}
            />
            <div className="pt-3 border-t border-white/8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mono">{t("comm.commissionAmount")}</div>
              <div className="text-4xl font-bold text-lime-300 mt-2 mono" data-testid="calc-output-amount">
                {formatCurrency(result.commission_amount)}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-white/40 text-sm">…</div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-white/50 uppercase tracking-wider">{label}</span>
      <span className={`font-mono ${highlight ? "text-white font-bold" : "text-white/80"}`}>{value}</span>
    </div>
  );
}

// ============================================================
// Forms
// ============================================================
function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <Label className="text-white/70 text-xs">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="bg-white/5 border-white/10 text-white mt-1"
      />
    </div>
  );
}

function SupplierForm({ isAdmin, agents, onClose, onCreated, t }) {
  const [form, setForm] = useState({
    name: "",
    country: "",
    contact_email: "",
    modality: "tech",
    agent_id: "",
    notes: "",
    is_paired: false,
    prospection_agent_id: "",
    closing_agent_id: "",
    split_pct: 50, // percentage stored as int for the slider; converted to 0..1 on submit
  });
  const [submitting, setSubmitting] = useState(false);

  const pairedError =
    form.is_paired &&
    form.prospection_agent_id &&
    form.closing_agent_id &&
    form.prospection_agent_id === form.closing_agent_id;

  const submit = async (e) => {
    e.preventDefault();
    if (pairedError) {
      toast.error(t("comm.pairedSameAgent"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!isAdmin) {
        delete payload.agent_id;
        delete payload.is_paired;
        delete payload.prospection_agent_id;
        delete payload.closing_agent_id;
        delete payload.split_pct;
      } else if (form.is_paired) {
        // Backend expects split_pct in 0..1
        payload.split_pct = Math.max(0.05, Math.min(0.95, (form.split_pct || 50) / 100));
        // When paired, agent_id is derived server-side from prospection_agent_id
        delete payload.agent_id;
      } else {
        delete payload.prospection_agent_id;
        delete payload.closing_agent_id;
        delete payload.split_pct;
      }
      await crmApi.post("/commissions/suppliers", payload);
      toast.success("✓");
      onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "supplier_already_registered") {
        toast.error("Proveedor ya registrado por otro agente");
      } else {
        toast.error(typeof detail === "string" ? detail : t("common.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title={t("comm.newSupplier")} testid="supplier-modal">
      <form onSubmit={submit} className="space-y-3">
        <Field label={t("comm.supplierName")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="sup-name" />
        <Field label={t("comm.supplierCountry")} value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="sup-country" />
        <Field label={t("comm.supplierEmail")} type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="sup-email" />
        <div>
          <Label className="text-white/70 text-xs">{t("comm.modality")}</Label>
          <select
            value={form.modality}
            onChange={(e) => setForm({ ...form, modality: e.target.value })}
            data-testid="sup-modality"
            className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
          >
            <option value="tech">{t("comm.modalityTech")}</option>
            <option value="general">{t("comm.modalityGeneral")}</option>
          </select>
        </div>
        {isAdmin && !form.is_paired && (
          <div>
            <Label className="text-white/70 text-xs">{t("comm.attribution")}</Label>
            <select
              value={form.agent_id}
              onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
              data-testid="sup-agent"
              className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
              required={!form.is_paired}
            >
              <option value="">{t("comm.pickAgent")}</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.full_name}</option>
              ))}
            </select>
          </div>
        )}
        {isAdmin && (
          <div className="border border-amber-500/20 rounded-lg p-3 bg-amber-500/5 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="sup-paired-toggle-label">
              <input
                type="checkbox"
                checked={form.is_paired}
                onChange={(e) => setForm({ ...form, is_paired: e.target.checked })}
                data-testid="sup-is-paired"
                className="w-4 h-4 accent-amber-400"
              />
              <span className="text-sm text-white font-medium flex items-center gap-2">
                <Users className="w-3.5 h-3.5 text-amber-300" />
                {t("comm.pairedToggle")}
              </span>
            </label>
            {form.is_paired && (
              <>
                <p className="text-[11px] text-white/50">{t("comm.pairedHint")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-white/70 text-xs">{t("comm.prospectionAgent")} *</Label>
                    <select
                      value={form.prospection_agent_id}
                      onChange={(e) => setForm({ ...form, prospection_agent_id: e.target.value })}
                      data-testid="sup-prospection-agent"
                      required
                      className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
                    >
                      <option value="">{t("comm.pickAgent")}</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>{a.full_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">{t("comm.closingAgent")} *</Label>
                    <select
                      value={form.closing_agent_id}
                      onChange={(e) => setForm({ ...form, closing_agent_id: e.target.value })}
                      data-testid="sup-closing-agent"
                      required
                      className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
                    >
                      <option value="">{t("comm.pickAgent")}</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>{a.full_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {pairedError && (
                  <p className="text-[11px] text-red-300" data-testid="sup-paired-same-agent-err">
                    {t("comm.pairedSameAgent")}
                  </p>
                )}
                <div>
                  <Label className="text-white/70 text-xs flex justify-between items-center">
                    <span>{t("comm.splitPct")}</span>
                    <span className="mono text-amber-300" data-testid="sup-split-display">
                      {form.split_pct}% / {100 - form.split_pct}%
                    </span>
                  </Label>
                  <input
                    type="range"
                    min={5}
                    max={95}
                    step={5}
                    value={form.split_pct}
                    onChange={(e) => setForm({ ...form, split_pct: parseInt(e.target.value, 10) })}
                    data-testid="sup-split-range"
                    className="w-full mt-2 accent-amber-400"
                  />
                  <p className="text-[10px] text-white/40 mt-1">{t("comm.splitHint")}</p>
                </div>
              </>
            )}
          </div>
        )}
        <div>
          <Label className="text-white/70 text-xs">{t("comm.notes")}</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="bg-white/5 border-white/10 text-white mt-1"
          />
        </div>
        <FormButtons onCancel={onClose} submitting={submitting || pairedError} t={t} testid="sup-submit" />
      </form>
    </Modal>
  );
}

function OperationForm({ suppliers, onClose, onCreated, t }) {
  const [form, setForm] = useState({
    supplier_id: "",
    gtx_operation_code: "",
    modality: "tech",
    units: 0,
    operation_value: 0,
    gtx_fee_override: "",
    invoiced_date: "",
    collected_date: "",
    notes: "",
  });
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Auto-set modality from supplier choice
  useEffect(() => {
    if (form.supplier_id) {
      const s = suppliers.find((x) => x.id === form.supplier_id);
      if (s?.modality) setForm((f) => ({ ...f, modality: s.modality }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.supplier_id]);

  // Live preview
  useEffect(() => {
    const run = async () => {
      if (!form.supplier_id) return setPreview(null);
      try {
        const sup = suppliers.find((x) => x.id === form.supplier_id);
        const isFirst = !(sup?.operations_count > 0);
        let daysSince = null;
        if (sup?.first_close_date) {
          const diff = (Date.now() - new Date(sup.first_close_date).getTime()) / 86400000;
          daysSince = Math.round(diff);
        }
        const r = await crmApi.post("/commissions/calculate", {
          modality: form.modality,
          units: parseInt(form.units, 10) || 0,
          operation_value: parseFloat(form.operation_value) || 0,
          is_first_operation: isFirst,
          days_since_first_close: daysSince,
        });
        setPreview(r.data);
      } catch (err) {
        logDevError("OperationForm.preview", err);
      }
    };
    run();
  }, [form.supplier_id, form.modality, form.units, form.operation_value, suppliers]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        supplier_id: form.supplier_id,
        gtx_operation_code: form.gtx_operation_code,
        modality: form.modality,
        units: parseInt(form.units, 10) || 0,
        operation_value: parseFloat(form.operation_value) || 0,
        gtx_fee_override: form.gtx_fee_override === "" ? null : parseFloat(form.gtx_fee_override),
        invoiced_date: form.invoiced_date || null,
        collected_date: form.collected_date || null,
        notes: form.notes,
      };
      await crmApi.post("/commissions", payload);
      toast.success("✓");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title={t("comm.newOperation")} testid="op-modal" wide>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label className="text-white/70 text-xs">{t("comm.colSupplier")} *</Label>
          <select
            value={form.supplier_id}
            onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
            data-testid="op-supplier"
            required
            className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
          >
            <option value="">—</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} {s.country ? `· ${s.country}` : ""} ({s.modality})
              </option>
            ))}
          </select>
        </div>
        <Field label={t("comm.operationCode")} value={form.gtx_operation_code} onChange={(v) => setForm({ ...form, gtx_operation_code: v })} testid="op-code" />
        <div>
          <Label className="text-white/70 text-xs">{t("comm.modality")}</Label>
          <select
            value={form.modality}
            onChange={(e) => setForm({ ...form, modality: e.target.value })}
            data-testid="op-modality"
            className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
          >
            <option value="tech">{t("comm.modalityTech")}</option>
            <option value="general">{t("comm.modalityGeneral")}</option>
          </select>
        </div>
        {form.modality === "tech" ? (
          <Field label={t("comm.units")} type="number" value={form.units} onChange={(v) => setForm({ ...form, units: v })} testid="op-units" />
        ) : (
          <Field label={t("comm.operationValue")} type="number" value={form.operation_value} onChange={(v) => setForm({ ...form, operation_value: v })} testid="op-value" />
        )}
        <Field label={`${t("comm.gtxFee")} (override)`} type="number" value={form.gtx_fee_override} onChange={(v) => setForm({ ...form, gtx_fee_override: v })} testid="op-fee-override" />
        <Field label={t("comm.invoicedDate")} type="date" value={form.invoiced_date} onChange={(v) => setForm({ ...form, invoiced_date: v })} testid="op-invoiced" />
        <Field label={t("comm.collectedDate")} type="date" value={form.collected_date} onChange={(v) => setForm({ ...form, collected_date: v })} testid="op-collected" />

        {preview && (
          <div className="sm:col-span-2 rounded-lg border border-lime-400/30 bg-lime-400/[0.05] p-3 text-xs grid grid-cols-3 gap-3">
            <div>
              <div className="text-white/40">{t("comm.gtxFee")}</div>
              <div className="font-mono font-semibold text-white">{formatCurrency(preview.gtx_fee)}</div>
            </div>
            <div>
              <div className="text-white/40">{t("comm.commissionRate")}</div>
              <div className="font-mono font-semibold text-white">{Math.round((preview.commission_rate || 0) * 100)}%</div>
            </div>
            <div>
              <div className="text-white/40">{t("comm.commissionAmount")}</div>
              <div className="font-mono font-bold text-lime-300" data-testid="op-preview-amount">
                {formatCurrency(preview.commission_amount)}
              </div>
            </div>
          </div>
        )}

        <div className="sm:col-span-2">
          <Label className="text-white/70 text-xs">{t("comm.notes")}</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="bg-white/5 border-white/10 text-white mt-1"
          />
        </div>
        <div className="sm:col-span-2">
          <FormButtons onCancel={onClose} submitting={submitting} t={t} testid="op-submit" />
        </div>
      </form>
    </Modal>
  );
}

function PayoutForm({ agents, onClose, onCreated, t }) {
  const [form, setForm] = useState({
    agent_id: "",
    period_start: "",
    period_end: "",
    invoice_ref: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await crmApi.post("/commissions/payouts/generate", form);
      toast.success("✓");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("comm.noAccruedForAgent"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title={t("comm.generatePayout")} testid="payout-modal">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label className="text-white/70 text-xs">{t("comm.colAgent")} *</Label>
          <select
            value={form.agent_id}
            onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
            data-testid="payout-agent"
            required
            className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
          >
            <option value="">{t("comm.pickAgent")}</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.full_name}</option>
            ))}
          </select>
        </div>
        <Field label="Period start" type="date" value={form.period_start} onChange={(v) => setForm({ ...form, period_start: v })} testid="payout-start" />
        <Field label="Period end" type="date" value={form.period_end} onChange={(v) => setForm({ ...form, period_end: v })} testid="payout-end" />
        <Field label="Invoice ref" value={form.invoice_ref} onChange={(v) => setForm({ ...form, invoice_ref: v })} testid="payout-invoice" />
        <p className="text-xs text-white/40">{t("comm.payoutPeriod")}</p>
        <FormButtons onCancel={onClose} submitting={submitting} t={t} testid="payout-submit" />
      </form>
    </Modal>
  );
}

function Modal({ children, title, onClose, testid, wide }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`glass-strong rounded-2xl p-6 w-full ${wide ? "max-w-2xl" : "max-w-md"} border border-white/10 max-h-[90vh] overflow-y-auto`}
        data-testid={testid}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormButtons({ onCancel, submitting, t, testid }) {
  return (
    <div className="flex gap-2 pt-2">
      <Button type="button" variant="outline" onClick={onCancel} className="border-white/10 text-white/80">
        {t("common.cancel")}
      </Button>
      <Button
        type="submit"
        disabled={submitting}
        data-testid={testid}
        className="flex-1 bg-lime-400 hover:bg-lime-300 text-black font-semibold"
      >
        {submitting ? t("common.saving") : t("common.create")}
      </Button>
    </div>
  );
}
